import { featureModuleTypesEnum } from "helpers/enums/featureModuleTypes";
import { isHidden } from "helpers/hiddenData";
import { REPORT_TYPES } from "helpers/reports";
import { isOnlyInRole, RolesModel } from "helpers/roles";
import pundit from "./pundit";

type PathRoleName = keyof RolesModel;

const pathRequiredRoles = {
    bids: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isCargoOwnerAdmin",
        "isCargoOwnerManager",
        "isGuest",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
        "isTruckingOrganizationExtendedViewer",
    ],
    chats: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isCargoOwnerAdmin",
        "isCargoOwnerManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
        "isTruckingOrganizationExtendedViewer",
    ],
    distributionBids: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationExtendedViewer",
    ],
    trucks: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
        "isTruckingOrganizationExtendedViewer",
    ],
    drivers: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
        "isTruckingOrganizationExtendedViewer",
    ],
    trailers: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
        "isTruckingOrganizationExtendedViewer",
    ],
    hiredPark: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
        "isTruckingOrganizationExtendedViewer",
    ],
    transportColumns: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationExtendedViewer",
    ],
    dictionaries: ["isRoot", "isAdmin", "isIntegrationManager"],
    trackers: [
        "isRoot",
        "isTechSupport",
        "isAdmin",
        "isIntegrationManager",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
        "isTruckingOrganizationHeadLogist",
    ],
    cargoOwners: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
    ],
    reports: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isCargoOwnerAdmin",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationOperator",
    ],
    intersectionsReport: [
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationHeadLogist",
        "isTruckingOrganizationExtendedViewer",
    ],
    planning: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationExtendedViewer",
    ],
    performancePlans: ["isTruckingOrganizationAdmin", "isTruckingOrganizationHeadLogist"],
    calculationQueues: ["isRoot", "isTechSupport", "isIntegrationManager", "isDiagnostics"],
    adminUsers: ["isTechSupport", "isAdmin", "isRoot", "isAccountManager", "isIntegrationManager", "isDiagnostics"],
    userLogs: ["isRoot", "isTechSupport", "isIntegrationManager", "isDiagnostics"],
    customPoints: ["isRoot", "isAdmin", "isTruckingOrganizationAdmin", "isIntegrationManager"],
    itemChangePoints: ["isRoot", "isAdmin", "isTruckingOrganizationAdmin", "isIntegrationManager"],
    regionGroups: ["isRoot", "isTruckingOrganizationAdmin", "isTruckingOrganizationHeadLogist"],
    adminDrivers: ["isRoot", "isTechSupport", "isIntegrationManager", "isDiagnostics"],
    adminOrgs: ["isRoot", "isIntegrationManager", "isDiagnostics"],
    adminTrackerLogs: ["isRoot", "isTechSupport", "isIntegrationManager", "isDiagnostics"],
    adminFilterCoordinates: ["isRoot"],
    routeTemplates: ["isTruckingOrganizationAdmin", "isTruckingOrganizationHeadLogist"],
    organizationCatalogs: ["isTruckingOrganizationAdmin", "isIntegrationManager", "isTruckingOrganizationHeadLogist"],
    scenarios: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isIntegrationManager",
        "isTruckingOrganizationHeadLogist",
    ],
    tripScenarios: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isIntegrationManager",
        "isTruckingOrganizationHeadLogist",
    ],
    trackersInfo: ["isRoot", "isIntegrationManager", "isDiagnostics"],
    couplings: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
    ],
    notificationQueues: ["isRoot", "isTechSupport", "isIntegrationManager", "isDiagnostics"],
    news: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationDispatcher",
        "isTruckingOrganizationHeadLogist",
    ],
    clientStatistics: ["isRoot", "isIntegrationManager", "isAccountManager"],
    blockedEmails: ["isRoot", "isTechSupport", "isIntegrationManager", "isDiagnostics"],
    distributionBidSources: ["isRoot", "isIntegrationManager"],
    forwarding: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationHeadLogist",
        "isTruckingOrganizationOperator",
    ],
    fuelCards: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationHeadLogist",
        "isIntegrationManager",
    ],
    organizationActivityReport: ["isRoot", "isIntegrationManager", "isAccountManager"],
    driversStatsOfYearReport: ["isRoot", "isIntegrationManager", "isAccountManager"],
    fuelLimitsReport: ["isRoot", "isIntegrationManager", "isTechSupport"],
    suitableOrdersReport: ["isRoot", "isIntegrationManager", "isAccountManager"],
    adminPlanFactOverallReport: ["isRoot", "isIntegrationManager", "isAccountManager"],
    requestStatistics: ["isRoot", "isIntegrationManager"],
    fixRouteBids: ["isRoot", "isTechSupport", "isIntegrationManager", "isDiagnostics"],
    organizationFixRouteBids: [
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationHeadLogist",
    ],
    neuralNetworkLogs: ["isRoot", "isTechSupport", "isIntegrationManager"],
    mobileAppRatings: ["isRoot", "isTechSupport", "isIntegrationManager"],
    organizationRecommendations: ["isRoot", "isIntegrationManager", "isAccountManager"],
    dashboard: [
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationHeadLogist",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationExtendedViewer",
    ],
    cargorunIndex: [
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationHeadLogist",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
    ],
    tasks: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
        "isTruckingOrganizationExtendedViewer",
    ],
    legalPersons: ["isRoot", "isAdmin", "isTruckingOrganizationAdmin"],
    popups: ["isRoot", "isAdmin", "isIntegrationManager", "isTechSupport"],
    platonPoints: ["isRoot", "isAdmin", "isTruckingOrganizationAdmin", "isTruckingOrganizationHeadLogist"],
    emergencyAreaPoints: ["isTruckingOrganizationAdmin", "isTruckingOrganizationHeadLogist"],
    routeControlPoints: ["isTruckingOrganizationAdmin"],
    postamatPoints: ["isTruckingOrganizationAdmin", "isIntegrationManager"],
    adminPlatformNews: ["isRoot", "isAdmin", "isIntegrationManager", "isTechSupport", "isAccountManager"],
    paymentPoints: ["isRoot", "isAdmin", "isIntegrationManager", "isTechSupport"],
    emulatedSegmentsLogs: ["isRoot", "isAdmin", "isIntegrationManager", "isTechSupport"],
    preservedTrackerLogs: ["isRoot", "isAdmin", "isIntegrationManager", "isTechSupport"],
    adminTariffs: ["isRoot", "isAdmin"],
    reportAllowedUsers: ["isRoot"],
    feedbackTags: ["isRoot", "isAdmin"],
    usersGroups: ["isRoot", "isAdmin", "isTruckingOrganizationAdmin", "isTruckingOrganizationHeadLogist"],
    unstableZones: ["isRoot", "isAdmin"],
    regionDryingPeriods: ["isRoot", "isAdmin", "isIntegrationManager"],
    adminInsolvencyRegistry: ["isRoot", "isAdmin", "isIntegrationManager"],
    adminAccessPermits: ["isRoot", "isAdmin", "isIntegrationManager"],
    adminParcelStatuses: ["isRoot", "isAdmin", "isIntegrationManager"],
    assistant: ["isTruckingOrganizationAdmin"],
    integration: ["isTruckingOrganizationAdmin", "isTruckingOrganizationHeadLogist"],
    integrationClients: ["isRoot"],
    finances: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationHeadLogist",
        "isTruckingOrganizationManager",
    ],
    crStart: ["isRoot", "isAdmin", "isTruckingOrganizationAdmin", "isTruckingOrganizationHeadLogist"],
    documentNumberSettings: ["isRoot", "isAdmin", "isIntegrationManager", "isTruckingOrganizationAdmin"],
    connectKontur: ["isTruckingOrganizationAdmin"],
    accessPermits: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationOperator",
        "isIntegrationManager",
    ],
    waybills: [
        "isRoot",
        "isAdmin",
        "isTruckingOrganizationAdmin",
        "isTruckingOrganizationManager",
        "isTruckingOrganizationHeadLogist",
        "isTruckingOrganizationOperator",
        "isTruckingOrganizationViewer",
        "isTruckingOrganizationDispatcher",
    ],
} satisfies Record<string, PathRoleName[]>;

const PathPolicy = pundit({
    canSeeSettings: ({ user: { roles } }) => !isOnlyInRole(roles, "isTruckingOrganizationExtendedViewer"),
    canSeeBids: ({ user: { roles } }) => pathRequiredRoles.bids.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeDistributionBids: ({ user: { roles } }) =>
        pathRequiredRoles.distributionBids.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeChats: ({ user: { roles } }) => pathRequiredRoles.chats.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeTrucks: ({ user: { roles } }) =>
        pathRequiredRoles.trucks.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeDrivers: ({ user: { roles } }) =>
        pathRequiredRoles.drivers.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeTrailers: ({ user: { roles } }) =>
        pathRequiredRoles.trailers.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeHiredPark: ({ user: { roles } }) =>
        pathRequiredRoles.hiredPark.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeTransportColumns: ({ user: { roles } }) =>
        pathRequiredRoles.transportColumns.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeDictionaries: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.dictionaries.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeTrackers: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.trackers.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeIntegration: ({ user: { roles } }) =>
        pathRequiredRoles.integration.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeIntegrationClients: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.integrationClients.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeCargoOwners: ({ user: { roles } }) =>
        pathRequiredRoles.cargoOwners.some((roleName: keyof RolesModel) => roles[roleName]) && !isHidden(),
    canSeeReports: ({ user: { roles } }) =>
        pathRequiredRoles.reports.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeIntersections: ({ user: { roles } }) =>
        pathRequiredRoles.intersectionsReport.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeProfile: () => true,
    canSeeCalculationQueues: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.calculationQueues.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeNotificationQueues: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.notificationQueues.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminUsers: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.adminUsers.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeUserLogs: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.userLogs.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeCustomPoints: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.customPoints.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeItemChangePoints: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.itemChangePoints.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeRegionGroups: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.regionGroups.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminDrivers: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.adminDrivers.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminOrgs: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.adminOrgs.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminTrackerLogs: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.adminTrackerLogs.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminFilterCoordinates: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.adminFilterCoordinates.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeRouteTemplates: ({ user: { roles } }) =>
        pathRequiredRoles.routeTemplates.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeOrganizationCatalogs: ({ user: { roles } }) =>
        pathRequiredRoles.organizationCatalogs.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeTrackersInfo: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.trackersInfo.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeNews: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.news.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeClientStatistics: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.clientStatistics.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeBlockedEmails: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.blockedEmails.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeDistributionBidSources: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.distributionBidSources.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeOrganizationActivityReport: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.organizationActivityReport.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeDriversStatsOfYearReport: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.driversStatsOfYearReport.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeFuelLimitsReport: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.fuelLimitsReport.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeSuitableOrdersReport: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.suitableOrdersReport.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminPlanFactOverallReport: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.adminPlanFactOverallReport.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeRequestStatistics: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.requestStatistics.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeFixRouteBids: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.fixRouteBids.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeOrganizationFixRouteBids: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.organizationFixRouteBids.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeOrganizationRecommendations: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.organizationRecommendations.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeDashboard: ({ user: { roles } }) =>
        pathRequiredRoles.dashboard.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeCargorunIndex: ({ user: { roles } }) =>
        pathRequiredRoles.cargorunIndex.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeNeuralNetworkLogs: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.neuralNetworkLogs.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeMobileAppRatings: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.mobileAppRatings.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeTasks: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.tasks.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeLegalPersons: ({ user: { roles } }) =>
        pathRequiredRoles.legalPersons.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeePopups: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.popups.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeePlatonPoints: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.platonPoints.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeEmergencyAreaPoints: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.emergencyAreaPoints.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeRouteControlPoints: ({ user: { roles }, options: { availableFeatureModules, limitedFunctionality } }) =>
        !limitedFunctionality &&
        !!availableFeatureModules &&
        availableFeatureModules.includes(
            featureModuleTypesEnum.RouteControlPoints as keyof typeof featureModuleTypesEnum,
        ) &&
        pathRequiredRoles.routeControlPoints.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeePostamatPoints: ({ user: { roles }, options: { limitedFunctionality, availableFeatureModules } }) =>
        !limitedFunctionality &&
        !!availableFeatureModules &&
        availableFeatureModules.includes(
            featureModuleTypesEnum.DocumentAssignment as keyof typeof featureModuleTypesEnum,
        ) &&
        pathRequiredRoles.postamatPoints.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminPlatformNews: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.adminPlatformNews.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeePaymentPoints: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.paymentPoints.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeEmulatedSegmentsLogs: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.emulatedSegmentsLogs.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeePreservedTrackerLogs: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.preservedTrackerLogs.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeUserPlatformNews: () => true,
    canSeeAdminTariffs: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.adminTariffs.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeReportAllowedUsers: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.reportAllowedUsers.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminClientStatisticsReport: ({ user: { allowedReports }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && !!allowedReports?.includes(REPORT_TYPES.ClientStatisticsReport),
    canSeeFeedbackTags: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.feedbackTags.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeUnstableZones: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.unstableZones.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeRegionDryingPeriods: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.regionDryingPeriods.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeUsersGroups: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.usersGroups.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminInsolvencyRegistry: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.adminInsolvencyRegistry.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminAccessPermits: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.adminAccessPermits.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAdminParcelStatuses: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality &&
        pathRequiredRoles.adminParcelStatuses.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAccessPermits: ({ user: { roles }, options: { limitedFunctionality } }) =>
        !limitedFunctionality && pathRequiredRoles.accessPermits.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeWaybills: ({ user: { roles }, options: { availableFeatureModules, limitedFunctionality } }) =>
        !limitedFunctionality &&
        !!availableFeatureModules &&
        availableFeatureModules.includes(featureModuleTypesEnum.Waybills as keyof typeof featureModuleTypesEnum) &&
        pathRequiredRoles.waybills.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeCrStart: ({ user: { roles }, options: { availableFeatureModules } }) =>
        !!availableFeatureModules &&
        availableFeatureModules.includes(featureModuleTypesEnum.CrStart as keyof typeof featureModuleTypesEnum) &&
        pathRequiredRoles.crStart.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeDocumentNumberSettings: ({ user: { roles }, options: { availableFeatureModules } }) =>
        !!availableFeatureModules &&
        availableFeatureModules.includes(featureModuleTypesEnum.CrStart as keyof typeof featureModuleTypesEnum) &&
        pathRequiredRoles.documentNumberSettings.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeFinances: ({ user: { roles } }) =>
        pathRequiredRoles.finances.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeConnectKontur: ({ user: { roles }, options: { availableFeatureModules, limitedFunctionality } }) =>
        !limitedFunctionality &&
        !!availableFeatureModules &&
        availableFeatureModules.includes(
            featureModuleTypesEnum.KonturIntegration as keyof typeof featureModuleTypesEnum,
        ) &&
        pathRequiredRoles.connectKontur.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeAssistant: ({ user: { roles } }) =>
        pathRequiredRoles.assistant.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeForwarding: ({ user: { roles }, options: { availableFeatureModules } }) =>
        !!availableFeatureModules &&
        availableFeatureModules.includes(featureModuleTypesEnum.Forwarding as keyof typeof featureModuleTypesEnum) &&
        pathRequiredRoles.forwarding.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeTripScenarios: ({ user: { roles }, options: { availableFeatureModules, limitedFunctionality } }) =>
        !limitedFunctionality &&
        !!availableFeatureModules &&
        availableFeatureModules.includes(featureModuleTypesEnum.TripScenarios as keyof typeof featureModuleTypesEnum) &&
        pathRequiredRoles.tripScenarios.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeScenarios: ({ user: { roles }, options: { availableFeatureModules, limitedFunctionality } }) =>
        !limitedFunctionality &&
        !!availableFeatureModules &&
        availableFeatureModules.includes(featureModuleTypesEnum.Scenarios as keyof typeof featureModuleTypesEnum) &&
        pathRequiredRoles.scenarios.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeePlanning: ({ user: { roles }, options: { availableFeatureModules } }) =>
        !!availableFeatureModules &&
        availableFeatureModules.includes(
            featureModuleTypesEnum.PlanningCarSchedule as keyof typeof featureModuleTypesEnum,
        ) &&
        pathRequiredRoles.planning.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeePerformancePlans: ({ user: { roles } }) =>
        pathRequiredRoles.performancePlans.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeCouplings: ({ user: { roles }, options: { availableFeatureModules, limitedFunctionality } }) =>
        !limitedFunctionality &&
        !!availableFeatureModules &&
        availableFeatureModules.includes(featureModuleTypesEnum.TripCoupling as keyof typeof featureModuleTypesEnum) &&
        pathRequiredRoles.couplings.some((roleName: keyof RolesModel) => roles[roleName]),
    canSeeFuelCards: ({
        user: { roles },
        options: { availableFeatureModules, isFuelLimitEnabled, limitedFunctionality, refuelingSettings },
    }) =>
        !limitedFunctionality &&
        !!availableFeatureModules &&
        availableFeatureModules.includes(
            featureModuleTypesEnum.RefuelingPlanning as keyof typeof featureModuleTypesEnum,
        ) &&
        !!isFuelLimitEnabled &&
        !refuelingSettings?.hideRefuelingPlanning &&
        pathRequiredRoles.fuelCards.some((roleName: keyof RolesModel) => roles[roleName]),
});

export default PathPolicy;
